import React, { useState, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { 
  X, 
  Save, 
  RotateCcw, 
  Plus, 
  Trash2, 
  ShieldCheck, 
  FileText, 
  Check, 
  Building2, 
  SlidersHorizontal 
} from 'lucide-react';

const DEFAULT_RESIGNATION_CLAUSES = [
  { 
    id: 'clause-1',
    title: '사직의사 통보 및 희망 퇴사일', 
    content: '본인은 사유로 인하여 지정한 일자로 사직하고자 하오니 재가하여 주시기 바랍니다.' 
  },
  { 
    id: 'clause-2',
    title: '성실한 업무 및 산출물 인수인계 확약', 
    content: '퇴사일 전까지 담당 업무 및 프로젝트 산출물, 소스코드, 사내 협업툴 계정 일체를 후임자에게 성실히 인수인계 완료할 것을 확약합니다.' 
  },
  { 
    id: 'clause-3',
    title: '퇴사자 정보보호 및 영업비밀·지식재산 유지 서약 (NDA)', 
    content: '재직 중 지득한 회사의 기술상·경영상의 영업비밀, 고객 데이터, 소스코드 및 지식재산권을 퇴사 후에도 일체 외부에 누설하거나 부당하게 사용하지 않으며 관련 법령에 따른 민·형사상 책임을 준수합니다.' 
  },
  { 
    id: 'clause-4',
    title: '사내 비품, 장비 및 마스터 계정 반납', 
    content: '사원증, 법인카드, 업무용 PC/노트북 장비 및 클라우드 인프라/SaaS 마스터 접근 권한을 퇴사 즉시 원상태로 반납합니다.' 
  },
  { 
    id: 'clause-5',
    title: '금품 청산 및 4대보험 자격상실', 
    content: '퇴직금 및 잔여 연차정산 수당 등 금품청산은 근로기준법에 따라 퇴사일로부터 14일 이내 지급되며 4대보험 자격상실 절차를 확인합니다.' 
  }
];

const ResignationPolicyModal = ({ isOpen, onClose }) => {
  const { resignationPolicy, updateResignationPolicy } = useContext(AppContext);

  if (!isOpen) return null;

  const currentClauses = Array.isArray(resignationPolicy?.clauses) && resignationPolicy.clauses.length > 0 
    ? resignationPolicy.clauses 
    : DEFAULT_RESIGNATION_CLAUSES;

  const [clauses, setClauses] = useState(currentClauses);
  const [allowHandoverFiles, setAllowHandoverFiles] = useState(resignationPolicy?.allowHandoverFiles !== false);
  const [isSaved, setIsSaved] = useState(false);

  const handleAddClause = () => {
    const newClause = {
      id: `clause-${Date.now()}`,
      title: '신규 사직원 조항',
      content: '조항 내용을 입력해 주세요.'
    };
    setClauses(prev => [...prev, newClause]);
  };

  const handleUpdateClause = (index, field, value) => {
    setClauses(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleRemoveClause = (index) => {
    if (clauses.length <= 1) {
      alert('최소 1개 이상의 사직원 조항이 유지되어야 합니다.');
      return;
    }
    setClauses(prev => prev.filter((_, i) => i !== index));
  };

  const handleResetToDefault = () => {
    if (window.confirm('기본 표준 사직원 조항으로 초기화하시겠습니까?')) {
      setClauses(DEFAULT_RESIGNATION_CLAUSES);
      setAllowHandoverFiles(true);
    }
  };

  const handleSave = async () => {
    const payload = {
      clauses,
      allowHandoverFiles,
      updatedAt: new Date().toISOString()
    };
    if (updateResignationPolicy) {
      await updateResignationPolicy(payload);
    }
    setIsSaved(true);
    setTimeout(() => {
      setIsSaved(false);
      onClose();
    }, 800);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-xs flex items-center justify-center p-3 md:p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[92vh] flex flex-col shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        
        {/* Modal Header */}
        <div className="px-6 py-4 bg-slate-900 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-indigo-600 flex items-center justify-center text-white shadow-md">
              <SlidersHorizontal className="w-5 h-5" />
            </div>
            <div className="text-left">
              <h2 className="text-base font-bold">사직원 표준 양식 및 회사 정책 설정</h2>
              <p className="text-xs text-slate-400 mt-0.5">임직원이 사직원 작성 시 기본 적용되는 공통 서약 및 인수인계 조항을 관리합니다.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1 text-left">
          
          <div className="bg-indigo-50/60 border border-indigo-100 rounded-2xl p-4 flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-indigo-600 shrink-0 mt-0.5" />
            <div className="text-xs text-slate-700 leading-relaxed">
              <strong className="text-slate-900 font-bold block mb-0.5">통합 표준 사직원 정책 안내</strong>
              사직원 제출 시 <strong>표준 사직의사 통보, 후임자 인수인계 확약, 퇴사자 보안 및 비밀유지 서약(NDA), 비품 반납</strong>이 하나의 종합 문서로 자동 병합됩니다. 아래에서 회사의 정책에 맞게 기본 조항을 자유롭게 수정하거나 추가하실 수 있습니다.
            </div>
          </div>

          {/* Option: Handover Files */}
          <div className="border border-slate-200 rounded-2xl p-4 bg-slate-50 flex items-center justify-between">
            <div>
              <h3 className="text-xs font-bold text-slate-900">인수인계 파일 첨부 기능 활성화</h3>
              <p className="text-[11px] text-slate-500 mt-0.5">사직원 작성 시 후임자에게 인수인계 산출물 문서를 첨부하여 제출할 수 있도록 허용합니다.</p>
            </div>
            <input
              type="checkbox"
              checked={allowHandoverFiles}
              onChange={e => setAllowHandoverFiles(e.target.checked)}
              className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500"
            />
          </div>

          {/* Clauses List */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-slate-200 pb-2">
              <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                <FileText className="w-4 h-4 text-indigo-600" />
                표준 사직원 기본 서약 조항 목록 ({clauses.length}개)
              </h3>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleResetToDefault}
                  className="px-2.5 py-1 text-slate-600 hover:text-slate-900 bg-white border border-slate-300 rounded-lg text-xs font-bold flex items-center gap-1 hover:bg-slate-50"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  기본값 복원
                </button>
                <button
                  type="button"
                  onClick={handleAddClause}
                  className="px-2.5 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold flex items-center gap-1"
                >
                  <Plus className="w-3.5 h-3.5" />
                  조항 추가
                </button>
              </div>
            </div>

            <div className="space-y-3">
              {clauses.map((clause, idx) => (
                <div key={clause.id || idx} className="p-4 bg-white border border-slate-200 rounded-2xl space-y-3 hover:border-slate-300 transition-colors shadow-2xs">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-1">
                      <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-700 text-[11px] font-black flex items-center justify-center shrink-0">
                        {idx + 1}
                      </span>
                      <input
                        type="text"
                        value={clause.title}
                        onChange={e => handleUpdateClause(idx, 'title', e.target.value)}
                        placeholder="조항 제목"
                        className="text-xs font-bold text-slate-900 px-2.5 py-1.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 flex-1"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveClause(idx)}
                      className="p-1.5 text-slate-400 hover:text-rose-600 rounded-lg hover:bg-rose-50 transition-colors"
                      title="조항 삭제"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <textarea
                    rows={3}
                    value={clause.content}
                    onChange={e => handleUpdateClause(idx, 'content', e.target.value)}
                    placeholder="조항 상세 내용..."
                    className="w-full text-xs text-slate-700 p-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 resize-none leading-relaxed"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 rounded-xl hover:bg-slate-200"
          >
            취소
          </button>
          
          <button
            type="button"
            onClick={handleSave}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-xs shadow-md transition-all ${
              isSaved 
                ? 'bg-emerald-600 text-white' 
                : 'bg-indigo-600 hover:bg-indigo-700 text-white'
            }`}
          >
            {isSaved ? (
              <>
                <Check className="w-4 h-4" />
                저장 완료 (동기화됨)
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                회사 정책 저장 및 DynamoDB 동기화
              </>
            )}
          </button>
        </div>

      </div>
    </div>
  );
};

export default ResignationPolicyModal;
